import { useQuery } from "@tanstack/react-query";

import { profileApi } from "./api";

export type UserProfile = Awaited<ReturnType<typeof profileApi.lookupProfiles>>[string];

/** `/profile/lookup` rejects more ids than this in a single request. */
const LOOKUP_BATCH_SIZE = 100;

let pendingIds = new Set<string>();
let pendingLookup: Promise<Record<string, UserProfile>> | null = null;

const lookupPendingIds = async (): Promise<Record<string, UserProfile>> => {
  const ids = [...pendingIds];
  pendingIds = new Set();
  pendingLookup = null;

  const chunks: string[][] = [];
  for (let start = 0; start < ids.length; start += LOOKUP_BATCH_SIZE) {
    chunks.push(ids.slice(start, start + LOOKUP_BATCH_SIZE));
  }
  const results = await Promise.all(chunks.map((chunk) => profileApi.lookupProfiles(chunk)));

  return Object.assign({}, ...results) as Record<string, UserProfile>;
};

const lookupUserProfile = async (userId: string): Promise<UserProfile> => {
  pendingIds.add(userId);
  const lookup = (pendingLookup ??= new Promise((resolve, reject) => {
    setTimeout(() => void lookupPendingIds().then(resolve, reject));
  }));

  return (await lookup)[userId] ?? {};
};

/**
 * Resolves the Cognito profile (name/email/picture) behind a user id. Ids
 * requested in the same tick share one `/profile/lookup` round trip, so a table
 * of rows costs a single request while each id caches and re-renders on its own.
 */
export const useUserProfile = (
  userId: string | null | undefined,
): { profile: UserProfile | undefined; isLoading: boolean } => {
  const { data, isPending } = useQuery({
    queryKey: ["users", "profile", userId],
    queryFn: () => lookupUserProfile(userId ?? ""),
    enabled: !!userId,
  });

  return { profile: data, isLoading: !!userId && isPending };
};
